import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { CREATE_NEW_PROSPECT_LABELS_ID } from '../../../config/constants';
import { Box, Typography, Form, Loader, StatusBanner, useWatch } from '@lucid/lego';
import type { FormRef } from '@lucid/lego';
import { useLocaleNavigate } from '../../../hooks/useLocaleNavigate';
import { useStore } from '../../../store/store';
import { useUserIdentity } from '@lucid/portal-core';
import { useCreateProspect } from '../../../api/services/lupp/prospect/prospect';
import {
  useGetTestDriveAvailableSlots,
  useGetTestDriveVehicles,
} from '../../../api/services/lupp/test-drive/test-drive';
import type {
  TestDriveSlotDetail,
  TestDriveVehicle,
} from '../../../api/services/lupp/test-drive/dto.test-drive';
import type {
  CreateProspectRequest,
  CreateProspectResponse,
  TestDriveSchedule,
} from '../../../api/services/lupp/prospect/dto.prospect';
import { useLabels } from '../../../hooks/useLabels';
import {
  convertToContentfulLocale,
  formatToUTCDate,
  localToUTC,
  toZuluDatetime,
  utcToZonedTimeSlot,
  zonedToUTC,
} from '../../../utils/tools';
import { createLeadFormFields } from './formFields';
import styles from './CreateNewProspect.module.scss';

const REDIRECT_DELAY = 3000;
const REDIRECT_PATH = '/prospect-desk/prospects';

const isValidTimeSlot = (time: string): boolean => /^\d{1,2}:\d{2}$/.test(time);

type TestDriveScheduleContext = {
  selectedVehicle?: TestDriveVehicle;
  availableSlots?: TestDriveSlotDetail[];
  agencyTimeZone?: string;
  userPortalId?: string;
  businessOrganizationId?: string;
};

/**
 * Builds test drive schedule from form data
 * The Form component passes data keyed by apiKey, so we use those keys here
 */
const buildTestDriveSchedule = (
  formData: Record<string, unknown>,
  location: string,
  locale: string,
  isTestDriveFlag: boolean,
  context: TestDriveScheduleContext
): TestDriveSchedule[] | undefined => {
  const isTestDrive = formData.isTestDrive as string[] | undefined;
  if (!isTestDrive?.includes('yes')) return undefined;

  const { selectedVehicle, availableSlots, agencyTimeZone } = context;
  const timeSlot = (formData.testDriveTime as string) || '';
  const [startTime, endTime] = timeSlot.split('-').map((part) => part?.trim() ?? '');
  // No slot means nothing to schedule, e.g. the day had no availability at all.
  if (!isValidTimeSlot(startTime) || !isValidTimeSlot(endTime)) return undefined;

  const testDriveDate = formData.testDriveDate as string | Date | undefined;
  const baseDateStr = testDriveDate ? formatToUTCDate(testDriveDate) : formatToUTCDate(new Date());

  // Slots are offered in the agency's timezone, so convert against that rather
  // than the signed-in user's locale.
  const buildDatetime = (time: string): string => {
    const [hours, minutes] = time.split(':').map(Number);
    const utc = agencyTimeZone
      ? zonedToUTC(baseDateStr, hours, minutes, agencyTimeZone)
      : localToUTC(baseDateStr, hours, minutes, locale);
    return toZuluDatetime(utc);
  };

  // The chosen slot names the specific car, which is the only place eventConfigId and vin exist.
  const selectedSlot = availableSlots?.find(
    (slot) => utcToZonedTimeSlot(slot.startTime, slot.endTime, slot.timezone) === timeSlot
  );
  const slotVehicle =
    selectedSlot?.tdEventConfig?.find(
      (config) => config.model === selectedVehicle?.model && config.trim === selectedVehicle?.trim
    ) ?? selectedSlot?.tdEventConfig?.[0];

  return [
    {
      appointmentLocationName: location,
      scheduledStartDatetime: buildDatetime(startTime),
      scheduledEndDatetime: buildDatetime(endTime),
      testDriveStatus: (formData.status as string) || 'scheduled',
      eventConfigId: slotVehicle?.eventConfigId,
      vin: slotVehicle?.vin,
      make: slotVehicle?.make ?? selectedVehicle?.make,
      model: selectedVehicle?.model,
      trim: selectedVehicle?.trim ?? (formData.selectedModelTrim as string) ?? '',
      // A test drive booked from here is hosted by whoever is signed in.
      businessPartnerUserId: context.userPortalId,
      businessPartnerOrganizationId: context.businessOrganizationId,
      timezone: selectedSlot?.timezone ?? agencyTimeZone,
      isActive: isTestDriveFlag,
      isDeleted: false,
    },
  ];
};

const Create: React.FC = () => {
  const { userIdentity } = useUserIdentity();
  const theme = useStore((state) => state.theme);
  const locale = useStore((state) => state.locale);
  const language = useStore((state) => state.language);
  const navigate = useLocaleNavigate();
  const { mutate, isPending } = useCreateProspect();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [createdProspectId, setCreatedProspectId] = useState<string | null>(null);
  const messageRef = useRef<HTMLDivElement>(null);
  const formRef = useRef<FormRef>(null);

  // Determine the effective locale for the API call
  // For English language:
  // - NL region with a non-German locale → 'en-NL'
  // - German locale or any other region → 'en-US'
  // For other languages → use native locale
  const effectiveLocale =
    language === 'en'
      ? userIdentity?.countryCode?.toUpperCase() === 'NL' && locale.toLowerCase() !== 'de-de'
        ? 'en-NL'
        : 'en-US'
      : convertToContentfulLocale(locale);

  const { data: labels } = useLabels({
    id: CREATE_NEW_PROSPECT_LABELS_ID,
    locale: effectiveLocale,
    countryCode: userIdentity?.countryCode,
    language: null,
  });

  const handleCancel = useCallback(() => {
    navigate(REDIRECT_PATH);
  }, [navigate]);

  const location =
    userIdentity?.city && userIdentity?.countryCode
      ? `${userIdentity.city}, ${userIdentity.countryCode}`
      : userIdentity?.city || userIdentity?.countryCode || '—';
  const organizationName = userIdentity?.organizationName;
  const agencyId = userIdentity?.businessOrganizationId ?? '';

  const { data: testDriveVehicles } = useGetTestDriveVehicles({ agencyId });

  const selectedModelTrim = useWatch<string>(formRef, 'selectedModelTrim');
  const testDriveDate = useWatch<string>(formRef, 'testDriveDate');
  const isTestDriveChecked = Boolean(useWatch<string[]>(formRef, 'isTestDrive')?.includes('yes'));

  // The dropdown holds the vehicle's description, but slots are keyed by model + trim.
  const selectedVehicle = testDriveVehicles?.find(
    (vehicle) => vehicle.description === selectedModelTrim
  );

  const { data: availableSlots } = useGetTestDriveAvailableSlots({
    agencyId,
    model: selectedVehicle?.model ?? '',
    trim: selectedVehicle?.trim,
    selectedDate: testDriveDate ?? '',
  });

  // Slot times are wall-clock in the agency's timezone, which the slots carry.
  const agencyTimeZone = availableSlots?.slotDetails?.[0]?.timezone;

  // A slot only exists for one model/date pair, so drop it when either changes.
  const slotContextRef = useRef<string | null>(null);
  useEffect(() => {
    const slotContext = `${selectedModelTrim ?? ''}|${testDriveDate ?? ''}`;
    if (slotContextRef.current !== null && slotContextRef.current !== slotContext) {
      formRef.current?.setValue('testDriveTime', '', { shouldValidate: false });
    }
    slotContextRef.current = slotContext;
  }, [selectedModelTrim, testDriveDate]);

  const formFields = useMemo(
    () =>
      createLeadFormFields(
        {
          onCancel: handleCancel,
          location,
          organizationName,
          testDriveVehicles,
          availableSlots: availableSlots?.slotDetails,
          selection: { modelTrim: selectedModelTrim, date: testDriveDate },
          isTestDrive: isTestDriveChecked,
          countryCode: userIdentity?.countryCode,
        },
        labels,
        locale
      ),
    [
      handleCancel,
      location,
      organizationName,
      labels,
      locale,
      testDriveVehicles,
      availableSlots,
      selectedModelTrim,
      testDriveDate,
      isTestDriveChecked,
      userIdentity?.countryCode,
    ]
  );

  // Cleanup timeout on unmount to prevent memory leaks
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    if (successMessage) {
      timeoutId = setTimeout(() => {
        // Navigate to the created prospect if we have an ID, otherwise go to prospects list
        const redirectPath = createdProspectId
          ? `/prospect-desk/prospects/${createdProspectId}`
          : REDIRECT_PATH;
        navigate(redirectPath);
      }, REDIRECT_DELAY);
    }
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [successMessage, navigate, createdProspectId]);

  // Scroll to message when error or success message appears
  useEffect(() => {
    if ((errorMessage || successMessage) && messageRef.current) {
      messageRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [errorMessage, successMessage]);

  const handleSuccess = (data: unknown) => {
    setErrorMessage(null);
    const formData = data as Record<string, unknown>;
    const isTestDrive = (formData.isTestDrive as string[])?.includes('yes') ?? false;

    const { isEmailOptIn, callConsent, ...restFormData } = formData;

    const payload = {
      ...restFormData,
      isEmailOptIn: (isEmailOptIn as string[])?.includes('emailOptIn') || null,
      isCallOptIn: callConsent === 'callOptIn' || null,
      isCallOptOut: callConsent === 'callOptOut' || null,
      isTestDrive,
      prospectOwnerLocation: location,
      testDriveSchedules: buildTestDriveSchedule(formData, location || '', locale, isTestDrive, {
        selectedVehicle,
        availableSlots: availableSlots?.slotDetails,
        agencyTimeZone,
        userPortalId: userIdentity?.userPortalId,
        businessOrganizationId: userIdentity?.businessOrganizationId,
      }),
    } as CreateProspectRequest;

    mutate(payload, {
      onSuccess: (response: CreateProspectResponse) => {
        if (response.prospectIdentifier) {
          setCreatedProspectId(response.prospectIdentifier);
        }
        setSuccessMessage(
          labels?.['create.success'] ?? 'Prospect created successfully! Redirecting...'
        );
      },
      onError: (error: unknown) => {
        setSuccessMessage('');
        // API errors are returned as strings from the LUPP service
        const errorMsg =
          typeof error === 'string'
            ? error
            : error instanceof Error
              ? error.message
              : (labels?.['create.error'] ?? 'Failed to create prospect. Please try again.');
        setErrorMessage(errorMsg);
      },
    });
  };

  const handleError = () => {
    setErrorMessage(
      labels?.['create.validationError'] ?? 'Please fix the form errors before submitting.'
    );
    setSuccessMessage('');
  };

  return (
    <div ref={messageRef}>
      <Typography variant="headline" size="small" color="primary-1" theme={theme}>
        {labels?.['create.createNewProspect'] ?? 'Create New Prospect'}
      </Typography>
      <Box pt={8}>
        <Typography variant="body" size="medium" color="gray-5" theme={theme}>
          {labels?.['create.addANew'] ?? 'Add a new prospect to your sales funnel'}
        </Typography>
      </Box>
      <Box className={styles.formContent} background={theme === 'dark' ? 'white' : 'black'} mt={32}>
        <Box className={styles.formWrapper}>
          {successMessage && (
            <StatusBanner
              type="success"
              message={successMessage}
              dataTestId="create-prospect-success-message"
              className={styles.statusBanner}
            />
          )}
          {errorMessage && (
            <StatusBanner
              type="error"
              message={errorMessage}
              dataTestId="create-prospect-error-message"
              className={styles.statusBanner}
            />
          )}
          {isPending && (
            <Box className="fetchingOverlay">
              <Loader size={32} theme={theme === 'dark' ? 'light' : 'dark'} />
            </Box>
          )}
          <Form
            id="create-prospect-form"
            ref={formRef}
            theme={theme === 'dark' ? 'light' : 'dark'}
            formFields={formFields}
            onSuccess={handleSuccess}
            onError={handleError}
            isFluid={true}
            locale={locale}
            language={language ?? locale.split('-')[0]}
          />
        </Box>
      </Box>
    </div>
  );
};

export default Create;
